import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Producto, VarianteNueva } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle, type Entidad } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, porcentaje } from '@/lib/formato-tienda';
import { DetalleDeProducto, DetalleDeVenta, DetalleDePaca } from '@/components/datos/detalles';

/**
 * PRODUCTOS — el catálogo, en tabla.
 *
 * ═══ EL CÓDIGO ES LO IMPORTANTE DE ESTA PANTALLA ═══
 *
 * El `sku` no es un campo técnico: es EL CÓDIGO que Maciel ya le pone a cada
 * pieza y que va impreso en la esquina de la foto que publica en Instagram.
 * La clienta manda una captura, el agente lee ese código y consulta existencia
 * por él. Por eso es la primera columna, va en monoespaciada y es la fija.
 *
 * ═══ POR QUÉ «SIN COSTO» ES UNA VISTA Y NO UN DETALLE ═══
 *
 * Un producto sin costo no rompe nada: se vende igual. Lo que hace es mentir
 * en Finanzas, porque su margen entra como si la pieza fuera gratis. Tenerlo
 * como vista con su cuenta es la única forma de que eso no se acumule callado.
 */

interface FilaDeVariante {
  talla: string;
  stockInicial: string;
  precio: string;
  costo: string;
  aviso: string;
}

const VARIANTE_VACIA: FilaDeVariante = {
  talla: '',
  stockInicial: '0',
  precio: '',
  costo: '',
  aviso: '2',
};

const VISTAS = [
  { id: 'todos', etiqueta: 'Todos' },
  { id: 'sin-costo', etiqueta: 'Sin costo', ayuda: 'Su margen sale inflado en Finanzas' },
  { id: 'sin-tallas', etiqueta: 'Sin tallas', ayuda: 'El agente no los ofrece' },
  { id: 'agotados', etiqueta: 'Agotados' },
  { id: 'inactivos', etiqueta: 'Inactivos' },
] as const;

export default function Productos() {
  return (
    <ConDetalle render={(e) => <Detalle entidad={e} />}>
      <Contenido />
    </ConDetalle>
  );
}

function Detalle({ entidad }: { entidad: Entidad }) {
  if (entidad.tipo === 'producto') return <DetalleDeProducto id={entidad.id} />;
  if (entidad.tipo === 'venta') return <DetalleDeVenta id={entidad.id} />;
  if (entidad.tipo === 'paca') return <DetalleDePaca id={entidad.id} />;
  return <p className="text-sm text-slate-500">Nada que mostrar.</p>;
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { vista: 'todos', buscar: '', nuevo: '', categoria: '' },
    { vista: VISTAS.map((v) => v.id) },
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['productos', 'catalogo'],
    queryFn: () => tienda.listarProductos({ limit: 200 }),
  });
  const productos = useMemo(() => data ?? [], [data]);

  const piezasDe = (p: Producto) => p.variants.reduce((a, v) => a + Math.max(0, v.stock), 0);
  const sinCosto = productos.filter((p) => p.cost == null);
  const sinTallas = productos.filter((p) => p.variants.length === 0);
  const agotados = productos.filter((p) => p.variants.length > 0 && piezasDe(p) === 0);

  const filtrados = useMemo(() => {
    let salida = productos;
    const v = filtros.vista;
    if (v === 'sin-costo') salida = salida.filter((p) => p.cost == null);
    if (v === 'sin-tallas') salida = salida.filter((p) => p.variants.length === 0);
    if (v === 'agotados')
      salida = salida.filter((p) => p.variants.length > 0 && piezasDe(p) === 0);
    if (v === 'inactivos') salida = salida.filter((p) => !p.isActive);
    if (filtros.categoria) salida = salida.filter((p) => (p.category ?? '') === filtros.categoria);

    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter((p) =>
        `${p.name} ${p.sku ?? ''} ${p.category ?? ''} ${(p.aliasesJson ?? []).join(' ')}`
          .toLowerCase()
          .includes(q),
      );
    }
    return salida;
  }, [productos, filtros.vista, filtros.buscar, filtros.categoria]);

  // SUPERFILTRO (Josué, 14-oct): las pestañas de antes son la faceta «Estado»; se suma la categoría.
  const cuentaDe = (id: string) =>
    id === 'sin-costo' ? sinCosto.length : id === 'sin-tallas' ? sinTallas.length : id === 'agotados' ? agotados.length : productos.filter((p) => !p.isActive).length;
  const facetas: Faceta[] = [
    { clave: 'vista', titulo: 'Estado', opciones: VISTAS.filter((v) => v.id !== 'todos').map((v) => ({ valor: v.id, etiqueta: v.etiqueta, cuenta: cuentaDe(v.id) })) },
    { clave: 'categoria', titulo: 'Categoría', todas: 'Todas', opciones: opcionesDe(productos, (p) => p.category) },
  ];

  const columnas: Columna<Producto>[] = [
    {
      id: 'sku',
      titulo: 'Código',
      fija: true,
      ancho: 'w-28',
      valor: (p) => p.sku ?? '',
      celda: (p) =>
        p.sku ? (
          <EntityLink tipo="producto" id={p.id} etiqueta={p.sku} mono sinIcono />
        ) : (
          <span className="text-amber-600 dark:text-amber-500">sin código</span>
        ),
    },
    {
      id: 'nombre',
      titulo: 'Nombre',
      valor: (p) => p.name,
      celda: (p) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-900 dark:text-slate-100">{p.name}</p>
          {p.aliasesJson?.length > 0 && (
            <p className="truncate text-xs text-slate-400">
              también: {p.aliasesJson.join(', ')}
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'categoria',
      titulo: 'Categoría',
      valor: (p) => p.category ?? '',
      celda: (p) => (
        <span className="text-slate-600 dark:text-slate-300">{p.category ?? '—'}</span>
      ),
    },
    {
      id: 'tallas',
      titulo: 'Tallas',
      valor: (p) => p.variants.length,
      celda: (p) =>
        p.variants.length === 0 ? (
          <Badge variant="warning">sin tallas</Badge>
        ) : (
          <div className="flex flex-wrap gap-1">
            {p.variants.slice(0, 5).map((v) => (
              <span
                key={v.id}
                title={v.stock <= 0 ? 'agotada' : `quedan ${v.stock}`}
                className={[
                  'rounded border px-1.5 py-0.5 text-xs tabular-nums',
                  v.stock <= 0
                    ? 'border-slate-200 text-slate-400 dark:border-slate-700'
                    : v.stock <= v.lowStockThreshold
                      ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
                      : 'border-surface-border text-slate-700 dark:text-slate-200',
                ].join(' ')}
              >
                {v.label} {v.stock <= 0 ? '·' : v.stock}
              </span>
            ))}
            {p.variants.length > 5 && (
              <span className="text-xs text-slate-400">+{p.variants.length - 5}</span>
            )}
          </div>
        ),
    },
    {
      id: 'piezas',
      titulo: 'Piezas',
      numerica: true,
      valor: (p) => piezasDe(p),
      celda: (p) => {
        const n = piezasDe(p);
        return n === 0 ? (
          <Badge variant="danger">0</Badge>
        ) : (
          <span className="tabular-nums text-slate-900 dark:text-slate-100">{n}</span>
        );
      },
      total: (fs) => numero(fs.reduce((a, p) => a + piezasDe(p), 0)),
    },
    {
      id: 'precio',
      titulo: 'Precio',
      numerica: true,
      valor: (p) => p.price,
      celda: (p) => dinero(p.price),
    },
    {
      id: 'costo',
      titulo: 'Costo',
      numerica: true,
      valor: (p) => p.cost ?? -1,
      celda: (p) =>
        p.cost == null ? (
          <span className="text-amber-600 dark:text-amber-500">sin costo</span>
        ) : (
          dinero(p.cost)
        ),
    },
    {
      id: 'margen',
      titulo: 'Margen',
      numerica: true,
      // `null` (desconocido) se ordena por debajo de cualquier margen real:
      // no es «margen cero», es «todavía no se sabe».
      valor: (p) => (p.cost == null || p.price <= 0 ? -1 : (p.price - p.cost) / p.price),
      celda: (p) => {
        if (p.cost == null || p.price <= 0) return <span className="text-slate-400">—</span>;
        const m = (p.price - p.cost) / p.price;
        return (
          <span
            className={
              m < 0.2
                ? 'text-red-600 dark:text-red-400'
                : 'text-slate-900 dark:text-slate-100'
            }
          >
            {porcentaje(m)}
          </span>
        );
      },
    },
    {
      id: 'estado',
      titulo: 'Estado',
      ocultaPorDefecto: true,
      valor: (p) => (p.isActive ? 'activo' : 'inactivo'),
      celda: (p) =>
        p.isActive ? (
          <Badge variant="success">Activo</Badge>
        ) : (
          <Badge variant="warning">Inactivo</Badge>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Productos"
        subtitulo="Tu catálogo. El código es el mismo que sale en la esquina de la foto."
        acciones={
          <>
            <Button variant="outline" onClick={() => navigate('/productos/importar')}>
              <Upload className="h-4 w-4" />
              Cargar de un golpe
            </Button>
            <Button onClick={() => poner('nuevo', '1')}>
              <Plus className="h-4 w-4" />
              Nuevo producto
            </Button>
          </>
        }
        kpis={
          <>
            <Kpi
              titulo="Productos"
              valor={numero(productos.length)}
              pie={`${productos.reduce((a, p) => a + p.variants.length, 0)} tallas`}
              onClick={() => poner('vista', 'todos')}
            />
            <Kpi
              titulo="Piezas"
              valor={numero(productos.reduce((a, p) => a + piezasDe(p), 0))}
              pie="en existencia"
            />
            <Kpi
              titulo="Sin costo"
              valor={numero(sinCosto.length)}
              pie="su margen sale inflado"
              tono={sinCosto.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'sin-costo')}
            />
            <Kpi
              titulo="Agotados"
              valor={numero(agotados.length)}
              pie="no se pueden ofrecer"
              tono={agotados.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'agotados')}
            />
          </>
        }
      />

      <DataTable
        filas={filtrados}
        columnas={columnas}
        idDe={(p) => p.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por código, nombre o como le dicen las clientas…"
        facetas={facetas}
        valoresDeFacetas={{ ...filtros, vista: filtros.vista === 'todos' ? undefined : filtros.vista }}
        onFacetas={ponerVarios}
        onLimpiarFiltros={() => ponerVarios(sinFiltros(facetas))}
        resultados={textoDeResultados(filtrados.length, productos.length, ['producto', 'productos'])}
        memoria="productos"
        exportarComo="productos"
        onAbrirFila={(p) =>
          abrir?.({ tipo: 'producto', id: p.id, etiqueta: p.name, detalle: p.sku ?? undefined })
        }
        vacio={{
          titulo: filtros.buscar ? `Nada con "${filtros.buscar}"` : 'Todavía no hay productos',
          descripcion: filtros.buscar
            ? 'Prueba con el código de la pieza, o con parte del nombre.'
            : 'Carga la primera pieza con su código, su precio y sus tallas.',
          accion: filtros.buscar ? undefined : (
            <div className="flex gap-2">
              <Button onClick={() => navigate('/productos/importar')}>Cargar de un golpe</Button>
              <Button variant="outline" onClick={() => poner('nuevo', '1')}>
                Una sola pieza
              </Button>
            </div>
          ),
        }}
      />

      <ModalNuevoProducto
        abierto={filtros.nuevo === '1'}
        onCerrar={() => poner('nuevo', undefined)}
        onCreado={() => {
          poner('nuevo', undefined);
          toast.success('Producto creado');
          void qc.invalidateQueries({ queryKey: ['productos'] });
          void qc.invalidateQueries({ queryKey: ['inventario'] });
        }}
      />
    </div>
  );
}

function ModalNuevoProducto({
  abierto,
  onCerrar,
  onCreado,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [aliases, setAliases] = useState('');
  const [variantes, setVariantes] = useState<FilaDeVariante[]>([{ ...VARIANTE_VACIA }]);

  const crear = useMutation({
    mutationFn: () => {
      const vs: VarianteNueva[] = variantes
        .filter((v) => v.talla.trim())
        .map((v) => ({
          atributos: { talla: v.talla.trim() },
          price: v.precio ? Number(v.precio) : null,
          cost: v.costo ? Number(v.costo) : null,
          stockInicial: v.stockInicial ? Number(v.stockInicial) : 0,
          lowStockThreshold: v.aviso ? Number(v.aviso) : 0,
        }));
      return tienda.crearProducto({
        name: name.trim(),
        sku: sku.trim(),
        category: category.trim() || undefined,
        price: Number(price),
        cost: cost ? Number(cost) : null,
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        variantes: vs,
      });
    },
    onSuccess: () => {
      setName('');
      setSku('');
      setCategory('');
      setPrice('');
      setCost('');
      setAliases('');
      setVariantes([{ ...VARIANTE_VACIA }]);
      onCreado();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear'),
  });

  const listo = name.trim() && sku.trim() && price !== '' && variantes.some((v) => v.talla.trim());

  return (
    <Modal open={abierto} onClose={onCerrar} title="Nuevo producto">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Código de la pieza
          </label>
          <Input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="005"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-slate-500">
            El mismo que le pones en la esquina de la foto. Por ahí lo busca el asistente cuando la
            clienta manda la captura.
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Nombre
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Vestido rosado de cuadritos"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Precio de venta
            </label>
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="1800"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Costo <span className="font-normal text-slate-400">(opcional)</span>
            </label>
            <Input
              type="number"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="900"
            />
          </div>
        </div>
        <p className="-mt-2 text-xs text-slate-500">
          Sin el costo no se puede calcular cuánto ganas con esta pieza. Se puede cargar después, o
          dejar que lo reparta la paca.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Categoría
            </label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Vestidos"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Como lo llaman las clientas
            </label>
            <Input
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="el rosado, de cuadritos"
            />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Tallas y existencia
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVariantes((v) => [...v, { ...VARIANTE_VACIA }])}
            >
              <Plus className="h-3.5 w-3.5" />
              Agregar talla
            </Button>
          </div>

          <div className="space-y-2">
            {variantes.map((v, i) => (
              <div key={i} className="grid grid-cols-[1fr_5rem_5rem] gap-2">
                <Input
                  value={v.talla}
                  onChange={(e) =>
                    setVariantes((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, talla: e.target.value } : p)),
                    )
                  }
                  placeholder="M (o «Única»)"
                />
                <Input
                  type="number"
                  value={v.stockInicial}
                  onChange={(e) =>
                    setVariantes((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, stockInicial: e.target.value } : p)),
                    )
                  }
                  placeholder="cant."
                />
                <Input
                  type="number"
                  value={v.aviso}
                  onChange={(e) =>
                    setVariantes((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, aviso: e.target.value } : p)),
                    )
                  }
                  placeholder="aviso"
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            La última columna es el aviso: cuando queden esas o menos, te lo marcamos para que le
            quites la publicidad.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={!listo || crear.isPending}>
            {crear.isPending ? 'Creando…' : 'Crear producto'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
